"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useQuery } from "@tanstack/react-query";
import { DEFAULT_SYMBOL, fetchOrderbook } from "@/lib/api";
import { useMacro, usePeg } from "@/lib/useIntel";
import AnimatedPrice from "../AnimatedPrice";
import ConnectButton from "../ConnectButton";
import DubuLogo from "../DubuLogo";
import ThemeToggle from "../ThemeToggle";

// Landing (/): the marketing face of swap.dubupay.com. The app lives at /trade.
// Everything on this page is REAL data: the hero price is the live book mid, the badges are
// the live peg monitor, the odds are live prediction markets. A trading product's landing
// that shows fake numbers teaches visitors to distrust the real ones.

// 3D loads client-side only, and only when motion is allowed.
const HeroOrb = dynamic(() => import("./HeroOrb"), { ssr: false });

const FEATURES = [
  {
    title: "Your wallet is your account",
    body: "No signup, no passwords. Connect MetaMask, Phantom or Petra and sign one message, on EVM, Solana or Aptos.",
  },
  {
    title: "The real USD/NGN rate",
    body: "A live order book, not a hidden markup. See the spread, the depth and every trade as it prints.",
  },
  {
    title: "Bridge between chains",
    body: "Move USDT and USDC across Ethereum, Polygon, Base, Optimism, BNB Chain and Solana. Your wallet signs; funds never pass through us.",
  },
  {
    title: "Naira rails, coming soon",
    body: "Fund with a bank transfer, withdraw to any Nigerian bank. In the works with our settlement partner.",
  },
];

// prefers-reduced-motion as an external store. The OS setting can change mid-session, and
// subscribing beats a mount-time setState (which the linter rightly flags as a cascade).
const motionQuery = () =>
  typeof window !== "undefined" ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
const subscribeMotion = (cb: () => void) => {
  const mq = motionQuery();
  mq?.addEventListener("change", cb);
  return () => mq?.removeEventListener("change", cb);
};
const useMotionOk = () =>
  useSyncExternalStore(subscribeMotion, () => !motionQuery()?.matches, () => false);

const Landing = () => {
  const motionOk = useMotionOk();
  const featuresRef = useRef<HTMLDivElement>(null);

  const { data: book } = useQuery({
    queryKey: ["orderbook", DEFAULT_SYMBOL],
    queryFn: ({ signal }) => fetchOrderbook(DEFAULT_SYMBOL, 12, signal),
    refetchInterval: 5_000,
  });
  const { data: peg } = usePeg();
  const { data: macro } = useMacro();

  const mid = book?.stats ? Number(book.stats.mid_price) : null;
  const spreadPct = book?.stats?.spread_percent;

  // Price direction drives the orb's mood; default rising. Tracked in an effect, mutating
  // refs during render breaks React's purity rules (and the linter rightly rejects it).
  const [direction, setDirection] = useState(1);
  const lastMid = useRef<number | null>(null);
  useEffect(() => {
    if (mid === null) return;
    if (lastMid.current !== null && mid !== lastMid.current) {
      setDirection(mid > lastMid.current ? 1 : -1);
    }
    lastMid.current = mid;
  }, [mid]);

  // Scroll-driven reveals for the feature cards.
  useEffect(() => {
    if (!motionOk || !featuresRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const cards = featuresRef.current!.querySelectorAll("[data-card]");
      gsap.from(cards, {
        opacity: 0,
        y: 28,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: { trigger: featuresRef.current, start: "top 75%" },
      });
    });
    return () => mm.revert();
  }, [motionOk]);

  const pegOk = peg?.stables?.every((s) => s.status === "ok");
  const fed = macro?.items?.find((m) => m.id === "fed");

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-[1200px] flex-col px-4">
      <header className="flex items-center justify-between gap-4 py-4">
        <DubuLogo />
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <ConnectButton />
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative flex min-h-[70vh] flex-col items-center justify-center overflow-hidden rounded-2xl border border-line bg-surface text-center">
        {/* 3D particle orb behind the copy; CSS glow as the no-motion fallback. */}
        {motionOk ? (
          <HeroOrb direction={direction} />
        ) : (
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,hsl(var(--brand-primary)/0.15),transparent_65%)]"
          />
        )}

        <div className="relative z-10 flex flex-col items-center gap-5 px-6 py-16">
          <h1 className="max-w-2xl text-4xl font-bold leading-tight tracking-tight text-fg sm:text-5xl">
            Trade USD&thinsp;/&thinsp;NGN at the{" "}
            <span className="text-brand">real rate</span>
          </h1>
          <p className="max-w-xl text-sm font-medium text-fg sm:text-base">
            A wallet-first exchange for dollars and naira, live order book, on-chain
            deposits, cross-chain bridging. No signup. No hidden spread.
          </p>

          {/* Live price chip, real book mid, ticking. */}
          <div className="flex items-baseline gap-3 rounded-full border border-line bg-page/70 px-5 py-2.5 backdrop-blur">
            <span className="text-xs text-fg-muted">USD / NGN</span>
            <AnimatedPrice value={mid} className="text-xl text-fg" />
            {spreadPct && (
              <span className="tnum text-[11px] text-fg-muted">spread {spreadPct}%</span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
            <Link
              href="/trade"
              className="rounded-md bg-brand px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-brand-glow"
            >
              Launch app
            </Link>
            <Link
              href="/bridge"
              className="rounded-md border border-line bg-surface px-6 py-3 text-sm text-fg transition-colors hover:bg-raised"
            >
              Bridge assets
            </Link>
          </div>

          {/* Live trust signals, real peg monitor + real prediction-market odds. */}
          <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-[11px] text-fg-muted">
            {pegOk && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-buy/30 bg-buy/10 px-2 py-0.5 text-buy">
                <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                USDT &amp; USDC on peg
              </span>
            )}
            {fed && (
              <span className="rounded-full border border-line bg-page/70 px-2 py-0.5">
                {fed.label}: <span className="tnum text-fg-secondary">{Math.round(fed.odds * 100)}%</span>{" "}
               · {fed.question}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────────────── */}
      <section ref={featuresRef} className="grid gap-4 py-12 sm:grid-cols-2">
        {FEATURES.map((f) => (
          <div
            key={f.title}
            data-card
            className="rounded-xl border border-line bg-surface p-5 transition-colors hover:border-brand/40"
          >
            <h2 className="text-sm font-semibold text-fg">{f.title}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-fg-secondary">{f.body}</p>
          </div>
        ))}
      </section>

      <footer className="flex items-center justify-between border-t border-line py-6 text-[11px] text-fg-muted">
        <span>© {new Date().getFullYear()} Dubu, part of the Dubu Pay family</span>
        <div className="flex gap-4">
          <Link href="/trade" className="hover:text-fg">Trade</Link>
          <Link href="/bridge" className="hover:text-fg">Bridge</Link>
        </div>
      </footer>
    </main>
  );
};

export default Landing;
